// Shared pieces for the support ticket pages (T1; attachments T3; category
// forms T4; language-filter reword UX T7).

import React from 'react';
import apiServices from '../../services/apiServices';
import { CheckLanguage, LanguageFilterHit } from '../../services/dtos';

// ── T4: category forms A–G (vision appendix) ────────────────────────────────
// Mirrors the server's TicketForms matrix (routing text per D-06: role
// labels, never person names). The server re-validates everything.

export interface TicketCategoryMeta {
  value: string;        // TicketCategories DTO value
  slug: string;         // /help/new/:slug
  title: string;
  blurb: string;        // picker card description
  destination: string;  // where it routes — role label per D-06
  evidence: boolean;    // A/B/F/G only
  confidential: boolean; // D/E only
}

export const TICKET_CATEGORIES: TicketCategoryMeta[] = [
  {
    value: 'WageDiscrepancy',
    slug: 'pay',
    title: 'Pay problem',
    blurb: 'Something looks wrong with pay from a call — short, missing, or miscalculated.',
    destination: 'Goes to the front desk',
    evidence: true,
    confidential: false,
  },
  {
    value: 'Contract',
    slug: 'contract',
    title: 'Contract question or violation',
    blurb: 'You are being asked to do something that may not be right under the contract — or you want to know what the contract says.',
    destination: 'Goes to the front desk',
    evidence: true,
    confidential: false,
  },
  {
    value: 'CallInfo',
    slug: 'call-info',
    title: 'Call information',
    blurb: 'A question about a call — times, location, details.',
    destination: 'Goes to dispatch',
    evidence: false,
    confidential: false,
  },
  {
    value: 'Organizing',
    slug: 'organizing',
    title: 'Organizing',
    blurb: 'A tip, a lead, or a question about organizing a shop. Can be submitted confidentially.',
    destination: 'Goes to the organizer',
    evidence: false,
    confidential: true,
  },
  {
    value: 'Political',
    slug: 'political',
    title: 'Political',
    blurb: 'Political and legislative questions or ideas. Can be submitted confidentially.',
    destination: 'Goes to the political director',
    evidence: false,
    confidential: true,
  },
  {
    value: 'Membership',
    slug: 'membership',
    title: 'Membership',
    blurb: 'Questions about your membership standing, dues, or status.',
    destination: 'Goes to the president',
    evidence: true,
    confidential: false,
  },
  {
    value: 'General',
    slug: 'general',
    title: 'General question',
    blurb: 'Anything else — if you are not sure where it goes, start here.',
    destination: 'Goes to the front desk',
    evidence: true,
    confidential: false,
  },
];

// The five ticket queues (T4) — queue name = role name that services it
// (mirrors the server's TicketQueues). Drives the /help/queue route gate
// and the queue page's tabs.
export interface TicketQueueMeta {
  name: string;
  label: string;
}

export const TICKET_QUEUES: TicketQueueMeta[] = [
  { name: 'FrontDesk', label: 'Front Desk' },
  { name: 'Dispatch', label: 'Dispatch' },
  { name: 'Organizer', label: 'Organizer' },
  { name: 'PoliticalDirector', label: 'Political Director' },
  { name: 'President', label: 'President' },
];

export const TICKET_QUEUE_ROLES = TICKET_QUEUES.map((q) => q.name);

// ── T5: escalation, staff lanes, delegation ────────────────────────────────

// D-06 role labels — roles, never person names, everywhere members can see.
export const ROLE_LABELS: Record<string, string> = {
  FrontDesk: 'Front Desk',
  Dispatch: 'Dispatch',
  Organizer: 'Organizer',
  PoliticalDirector: 'Political Director',
  President: 'President',
  BusinessRep: 'Business Representative',
  AsstBusinessRep: 'Assistant Business Representative',
};

export const roleLabel = (role?: string) => (role && ROLE_LABELS[role]) || role || '';

// Where a ticket can be escalated (mirrors TicketEscalationTargets).
export const ESCALATION_TARGETS = [
  'AsstBusinessRep',
  'BusinessRep',
  'Organizer',
  'PoliticalDirector',
  'President',
];

// Every staff lane: the five queues plus the two escalation-only targets.
// GetSupportQueue accepts all of them (a BR/ABR "queue" is their
// escalations); gates the queue page, board, snippets, and delegations.
export const TICKET_STAFF_ROLES = [...TICKET_QUEUE_ROLES, 'AsstBusinessRep', 'BusinessRep'];

// The lanes a viewer can open tabs for: their own lanes, plus the
// FrontDesk↔Dispatch mutual pair, plus everything for Admin. Cosmetic — the
// server re-checks every listing.
export function visibleLanes(roles: string[]): TicketQueueMeta[] {
  const lanes: TicketQueueMeta[] = [
    ...TICKET_QUEUES,
    { name: 'AsstBusinessRep', label: 'Escalations — Asst. Business Rep' },
    { name: 'BusinessRep', label: 'Escalations — Business Rep' },
  ];
  if (roles.includes('Admin')) return lanes;
  return lanes.filter(
    (l) =>
      roles.includes(l.name) ||
      (l.name === 'FrontDesk' && roles.includes('Dispatch')) ||
      (l.name === 'Dispatch' && roles.includes('FrontDesk'))
  );
}

export function TicketEscalatedBadge({ toRole }: { toRole?: string }) {
  if (!toRole) return null;
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
      Escalated · {roleLabel(toRole)}
    </span>
  );
}

// Member-facing status (vision Feature 6): WHERE the ticket stands, not the
// raw workflow status — "Received", "With the front desk", "Escalated to the
// Business Representative" (role labels per D-06), "Answered". Derived from
// status + queue + escalation; no schema.
const QUEUE_MEMBER_LABELS: Record<string, string> = {
  FrontDesk: 'With the front desk',
  Dispatch: 'With dispatch',
  Organizer: 'With the organizer',
  PoliticalDirector: 'With the political director',
  President: 'With the president',
};

export function memberStatusLabel(t: {
  status?: string;
  queue?: string;
  escalatedToRole?: string;
}): string {
  if (t.status === 'Dismissed') return 'Closed';
  if (t.status === 'Resolved') return 'Answered';
  if (t.escalatedToRole) return `Escalated to the ${roleLabel(t.escalatedToRole)}`;
  if (t.status === 'New') return 'Received';
  return QUEUE_MEMBER_LABELS[t.queue || ''] || 'With union staff';
}

export function MemberStatusBadge({
  ticket,
}: {
  ticket: { status?: string; queue?: string; escalatedToRole?: string };
}) {
  const styles: Record<string, string> = {
    New: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    InProgress:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    Resolved:
      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    Dismissed: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };
  const style =
    ticket.escalatedToRole && ticket.status !== 'Resolved' && ticket.status !== 'Dismissed'
      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      : styles[ticket.status || ''] ||
        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${style}`}
    >
      {memberStatusLabel(ticket)}
    </span>
  );
}

// ── T5: aging (board + queues) ─────────────────────────────────────────────
// "Answered" = staff have replied since the member last wrote. Aging clocks
// the wait from the member's side; the dot escalates ≥1 day amber, ≥3 days
// red. Cosmetic thresholds only.

export function ticketAnswered(t: {
  lastStaffReplyAtUtc?: string;
  lastMemberReplyAtUtc?: string;
}): boolean {
  if (!t.lastStaffReplyAtUtc) return false;
  if (!t.lastMemberReplyAtUtc) return true;
  return (
    parseServerDate(t.lastStaffReplyAtUtc).getTime() >=
    parseServerDate(t.lastMemberReplyAtUtc).getTime()
  );
}

export function ticketWaitingSince(t: {
  lastMemberReplyAtUtc?: string;
  createdAtUtc?: string;
}): Date {
  return parseServerDate(t.lastMemberReplyAtUtc || t.createdAtUtc || '');
}

export function fmtAge(from: Date): string {
  const ms = Date.now() - from.getTime();
  if (isNaN(ms) || ms < 0) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function TicketAgeDot({
  ticket,
}: {
  ticket: {
    status?: string;
    lastStaffReplyAtUtc?: string;
    lastMemberReplyAtUtc?: string;
    createdAtUtc?: string;
  };
}) {
  if (ticket.status === 'Resolved' || ticket.status === 'Dismissed') return null;
  const answered = ticketAnswered(ticket);
  const waiting = ticketWaitingSince(ticket);
  const hours = (Date.now() - waiting.getTime()) / 3600000;
  const color = answered
    ? 'bg-green-400'
    : hours >= 72
    ? 'bg-red-500'
    : hours >= 24
    ? 'bg-amber-400'
    : 'bg-gray-300 dark:bg-gray-600';
  const title = answered
    ? 'Answered — staff replied since the member last wrote'
    : `Waiting on staff for ${fmtAge(waiting)}`;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title={title}>
      <span className={`h-2 w-2 rounded-full ${color}`} />
      <span className="text-xs text-gray-500 dark:text-gray-400">
        {answered ? 'Answered' : `${fmtAge(waiting)} waiting`}
      </span>
    </span>
  );
}

export const categoryBySlug = (slug?: string) =>
  TICKET_CATEGORIES.find((c) => c.slug === slug);
export const categoryByValue = (value?: string) =>
  TICKET_CATEGORIES.find((c) => c.value === value);
export const ticketCategoryLabel = (value?: string) =>
  categoryByValue(value)?.title || value || '';

export function TicketCategoryBadge({ category }: { category?: string }) {
  if (!category) return null;
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
      {ticketCategoryLabel(category)}
    </span>
  );
}

export function TicketUrgentBadge({ isUrgent }: { isUrgent?: boolean }) {
  if (!isUrgent) return null;
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
      Happening now
    </span>
  );
}

export function TicketConfidentialBadge({ isConfidential }: { isConfidential?: boolean }) {
  if (!isConfidential) return null;
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
      Confidential
    </span>
  );
}

export const TICKET_STATUS_LABELS: Record<string, string> = {
  New: 'New',
  InProgress: 'In progress',
  Resolved: 'Resolved',
  Dismissed: 'Dismissed',
};

const STATUS_STYLES: Record<string, string> = {
  New: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  InProgress:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  Resolved:
    'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  Dismissed: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export function TicketStatusBadge({ status }: { status?: string }) {
  const s = status || '';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${
        STATUS_STYLES[s] ||
        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
      }`}
    >
      {TICKET_STATUS_LABELS[s] || s}
    </span>
  );
}

// The misuse disclaimer sits under EVERY composer in the ticket system
// (new ticket, reply, internal note) plus signup — the final sentence is the
// vision's exact wording (T4) and must not be softened.
export function MisuseDisclaimer() {
  return (
    <p className="text-xs text-gray-500 dark:text-gray-400">
      This system is for official union business. Messages are recorded and
      may be reviewed by union staff. Misuse of this system may result in
      referral hall write-ups and potential charges through the union process.
    </p>
  );
}

// Server timestamps are UTC; they arrive either as WCF "/Date(ms)/" (the
// ServiceStack default here) or as an ISO-ish string.
function parseServerDate(raw: string): Date {
  const wcf = /\/Date\((-?\d+)/.exec(raw);
  if (wcf) return new Date(parseInt(wcf[1], 10));
  const iso = raw.includes(' ') ? raw.replace(' ', 'T') : raw;
  return new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
}

export function fmtDateTime(raw?: string): string {
  if (!raw) return '—';
  const d = parseServerDate(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function fmtDate(raw?: string): string {
  if (!raw) return '—';
  const d = parseServerDate(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',   // dispatch dates are calendar dates, not instants
  });
}

// ── T3: evidence uploads ────────────────────────────────────────────────────

// Mirror of the server's A-07 rules (AttachmentRules.cs). Cosmetic only —
// the server re-validates every upload.
export const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.xlsx,.csv';
export const ATTACHMENT_MAX_BYTES = 15 * 1024 * 1024;

export function attachmentPrecheck(file: File): string | null {
  const ext = (file.name.match(/\.[^.]+$/)?.[0] || '').toLowerCase();
  if (!ATTACHMENT_ACCEPT.split(',').includes(ext))
    return 'File type not allowed — use pdf, jpg, png, heic, xlsx, or csv.';
  if (file.size > ATTACHMENT_MAX_BYTES) return 'File is too large — 15 MB maximum.';
  if (file.size === 0) return 'That file is empty.';
  return null;
}

export function fmtBytes(bytes?: number): string {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Cookie-JWT means plain links and fetch both authenticate.
export function attachmentUrl(id: number): string {
  return `${apiServices.apiUrl()}/support/attachment/${id}`;
}

// JsonServiceClient has no multipart path here — raw fetch with FormData.
// Throws with the server's message on failure.
export async function uploadTicketAttachment(
  ticketId: number,
  file: File
): Promise<void> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await fetch(
    `${apiServices.apiUrl()}/support/ticket/${ticketId}/attachment`,
    {
      method: 'POST',
      credentials: 'include',
      body: form,
      // without this ServiceStack content-negotiates errors into HTML
      headers: { Accept: 'application/json' },
    }
  );
  if (!res.ok) {
    let msg = `Upload failed (${res.status})`;
    try {
      const body = await res.json();
      msg = body?.responseStatus?.message || msg;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new Error(msg);
  }
}

// ── T7: language-filter inline reword UX (§7.1) ─────────────────────────────

export interface ComposerField {
  key: string;   // echoed back as LanguageFilterHit.field
  label: string;
  text: string;
}

// Pre-flight the composer's free-text fields against the server's scan so
// the member can reword BEFORE submitting. Fail-open on errors — submit and
// reply re-run the authoritative scan server-side regardless.
export async function checkLanguage(
  fields: ComposerField[]
): Promise<LanguageFilterHit[]> {
  const withText = fields.filter((f) => f.text && f.text.trim() !== '');
  if (withText.length === 0) return [];
  try {
    const req = new CheckLanguage();
    req.fields = Object.fromEntries(withText.map((f) => [f.key, f.text]));
    const res = await apiServices.client.post(req);
    return res.hits || [];
  } catch {
    return [];
  }
}

// "Please reword" with the offending spans indicated in place.
export function LanguageWarning({
  fields,
  hits,
}: {
  fields: ComposerField[];
  hits: LanguageFilterHit[];
}) {
  if (hits.length === 0) return null;
  const byField = fields
    .map((f) => ({ field: f, fieldHits: hits.filter((h) => h.field === f.key) }))
    .filter((x) => x.fieldHits.length > 0);
  return (
    <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-gray-900 p-4 text-sm flex flex-col gap-2">
      <p className="font-medium text-red-800 dark:text-red-300">
        Some of this can&rsquo;t be sent — please reword the highlighted parts
        and try again.
      </p>
      {byField.map(({ field, fieldHits }) => (
        <div key={field.key}>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {field.label}
          </div>
          <div className="whitespace-pre-wrap text-gray-900 dark:text-gray-100">
            <HighlightedText text={field.text} hits={fieldHits} />
          </div>
        </div>
      ))}
    </div>
  );
}

function HighlightedText({
  text,
  hits,
}: {
  text: string;
  hits: LanguageFilterHit[];
}) {
  // merge overlapping spans, then interleave plain and marked segments
  const merged: Array<{ start: number; end: number }> = [];
  for (const h of [...hits].sort((a, b) => a.start - b.start)) {
    const end = h.start + h.length;
    const last = merged[merged.length - 1];
    if (last && h.start <= last.end) last.end = Math.max(last.end, end);
    else merged.push({ start: h.start, end });
  }
  const parts: React.ReactNode[] = [];
  let pos = 0;
  merged.forEach((s, i) => {
    if (s.start > pos) parts.push(text.slice(pos, s.start));
    parts.push(
      <mark
        key={i}
        className="rounded bg-red-200 px-0.5 text-red-900 dark:bg-red-800 dark:text-red-100"
      >
        {text.slice(s.start, s.end)}
      </mark>
    );
    pos = s.end;
  });
  if (pos < text.length) parts.push(text.slice(pos));
  return <>{parts}</>;
}
